import React from 'react';
import { 
  VISUAL_STYLES, 
  LIGHTING_OPTIONS, 
  CAMERA_MOVEMENTS, 
  PERSON_GENERATION_OPTIONS,
  MOTION_INTENSITY_OPTIONS,
  MOOD_OPTIONS
} from '../../../services/veoService';

interface VeoAdvancedControlsProps {
  // Visual controls
  visualStyle: string;
  onVisualStyleChange: (value: string) => void;
  lighting: string;
  onLightingChange: (value: string) => void;
  cameraMovement: string;
  onCameraMovementChange: (value: string) => void;
  
  // Motion controls
  motionIntensity: string;
  onMotionIntensityChange: (value: string) => void;
  mood: string;
  onMoodChange: (value: string) => void;
  
  // Safety controls
  personGeneration: string;
  onPersonGenerationChange: (value: string) => void;
  negativePrompt: string;
  onNegativePromptChange: (value: string) => void;
  
  // Accordion state
  showVisualAccordion: boolean;
  onToggleVisualAccordion: () => void;
  showMotionAccordion: boolean;
  onToggleMotionAccordion: () => void;
  showSafetyAccordion: boolean;
  onToggleSafetyAccordion: () => void;
}

const VeoAdvancedControls: React.FC<VeoAdvancedControlsProps> = ({
  visualStyle,
  onVisualStyleChange,
  lighting,
  onLightingChange,
  cameraMovement,
  onCameraMovementChange,
  motionIntensity,
  onMotionIntensityChange,
  mood,
  onMoodChange,
  personGeneration,
  onPersonGenerationChange,
  negativePrompt,
  onNegativePromptChange,
  showVisualAccordion,
  onToggleVisualAccordion,
  showMotionAccordion,
  onToggleMotionAccordion,
  showSafetyAccordion,
  onToggleSafetyAccordion
}) => {
  const accordionHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    borderRadius: '6px',
    border: '1px solid rgba(139, 92, 246, 0.3)',
    cursor: 'pointer',
    marginBottom: '4px',
    transition: 'all 0.2s ease'
  };

  const accordionContentStyle = {
    padding: '16px',
    backgroundColor: '#1a1a1a',
    borderRadius: '6px',
    border: '1px solid rgba(139, 92, 246, 0.2)',
    marginBottom: '16px'
  };

  const selectStyle = {
    width: '100%',
    padding: '8px',
    border: '1px solid rgba(139, 92, 246, 0.3)',
    borderRadius: '4px',
    backgroundColor: '#1a1a1a',
    color: 'white',
    fontSize: '13px',
    outline: 'none'
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <h4 style={{ 
        margin: '0 0 16px 0', 
        fontSize: '16px', 
        fontWeight: '600',
        color: '#fff'
      }}>
        ⚙️ Advanced Veo Controls
      </h4>

      {/* Visual Style Accordion */}
      <div>
        <div 
          style={accordionHeaderStyle}
          onClick={onToggleVisualAccordion}
        >
          <span style={{ fontSize: '14px', fontWeight: '500', color: '#fff' }}>
            🎨 Visual Style & Camera
          </span>
          <span style={{ color: '#8b5cf6', fontSize: '18px' }}>
            {showVisualAccordion ? '−' : '+'}
          </span>
        </div>
        
        {showVisualAccordion && (
          <div style={accordionContentStyle}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px' 
            }}>
              {/* Visual Style */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '13px', 
                  fontWeight: '500',
                  color: '#fff'
                }}>
                  Visual Style:
                </label>
                <select
                  value={visualStyle}
                  onChange={(e) => onVisualStyleChange(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Default</option>
                  {VISUAL_STYLES.map((style) => (
                    <option key={style.id} value={style.id}>
                      {style.name}
                    </option>
                  ))}
                </select>
                {visualStyle && (
                  <p style={{ 
                    margin: '4px 0 0 0', 
                    fontSize: '11px', 
                    color: '#ccc'
                  }}>
                    {VISUAL_STYLES.find(s => s.id === visualStyle)?.description}
                  </p>
                )}
              </div>

              {/* Lighting */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '13px', 
                  fontWeight: '500',
                  color: '#fff'
                }}>
                  Lighting:
                </label>
                <select
                  value={lighting}
                  onChange={(e) => onLightingChange(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Default</option>
                  {LIGHTING_OPTIONS.map((light) => (
                    <option key={light.id} value={light.id}>
                      {light.name}
                    </option>
                  ))}
                </select>
                {lighting && (
                  <p style={{ 
                    margin: '4px 0 0 0', 
                    fontSize: '11px', 
                    color: '#ccc'
                  }}>
                    {LIGHTING_OPTIONS.find(l => l.id === lighting)?.description}
                  </p>
                )}
              </div>

              {/* Camera Movement */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '13px', 
                  fontWeight: '500',
                  color: '#fff'
                }}>
                  Camera Movement:
                </label>
                <select
                  value={cameraMovement}
                  onChange={(e) => onCameraMovementChange(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Default</option>
                  {CAMERA_MOVEMENTS.map((movement) => (
                    <option key={movement.id} value={movement.id}>
                      {movement.name}
                    </option>
                  ))}
                </select>
                {cameraMovement && (
                  <p style={{ 
                    margin: '4px 0 0 0', 
                    fontSize: '11px', 
                    color: '#ccc'
                  }}>
                    {CAMERA_MOVEMENTS.find(m => m.id === cameraMovement)?.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Motion & Mood Accordion */}
      <div>
        <div 
          style={accordionHeaderStyle}
          onClick={onToggleMotionAccordion}
        >
          <span style={{ fontSize: '14px', fontWeight: '500', color: '#fff' }}>
            🎭 Motion & Mood
          </span>
          <span style={{ color: '#8b5cf6', fontSize: '18px' }}>
            {showMotionAccordion ? '−' : '+'}
          </span>
        </div>
        
        {showMotionAccordion && (
          <div style={accordionContentStyle}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px' 
            }}>
              {/* Motion Intensity */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '13px', 
                  fontWeight: '500',
                  color: '#fff'
                }}>
                  Motion Intensity:
                </label>
                <select
                  value={motionIntensity}
                  onChange={(e) => onMotionIntensityChange(e.target.value)}
                  style={selectStyle}
                >
                  {MOTION_INTENSITY_OPTIONS.map((intensity) => (
                    <option key={intensity.id} value={intensity.id}>
                      {intensity.name}
                    </option>
                  ))}
                </select>
                {motionIntensity && (
                  <p style={{ 
                    margin: '4px 0 0 0', 
                    fontSize: '11px', 
                    color: '#ccc'
                  }}>
                    {MOTION_INTENSITY_OPTIONS.find(m => m.id === motionIntensity)?.description}
                  </p>
                )}
              </div>

              {/* Mood */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '13px', 
                  fontWeight: '500',
                  color: '#fff'
                }}>
                  Mood:
                </label>
                <select
                  value={mood}
                  onChange={(e) => onMoodChange(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Default</option>
                  {MOOD_OPTIONS.map((moodOption) => (
                    <option key={moodOption.id} value={moodOption.id}>
                      {moodOption.name}
                    </option>
                  ))}
                </select>
                {mood && (
                  <p style={{ 
                    margin: '4px 0 0 0', 
                    fontSize: '11px', 
                    color: '#ccc'
                  }}>
                    {MOOD_OPTIONS.find(m => m.id === mood)?.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Safety & Content Controls Accordion */}
      <div>
        <div 
          style={accordionHeaderStyle}
          onClick={onToggleSafetyAccordion}
        >
          <span style={{ fontSize: '14px', fontWeight: '500', color: '#fff' }}>
            🛡️ Safety & Content Controls
          </span>
          <span style={{ color: '#8b5cf6', fontSize: '18px' }}>
            {showSafetyAccordion ? '−' : '+'}
          </span>
        </div>
        
        {showSafetyAccordion && (
          <div style={accordionContentStyle}>
            {/* Person Generation */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '13px', 
                fontWeight: '500',
                color: '#fff'
              }}>
                Person Generation:
              </label>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                gap: '8px' 
              }}>
                {PERSON_GENERATION_OPTIONS.map((option) => {
                  const isSelected = personGeneration === option.id;
                  return (
                    <div
                      key={option.id}
                      onClick={() => onPersonGenerationChange(option.id)}
                      style={{
                        padding: '10px',
                        border: isSelected 
                          ? '2px solid #8b5cf6' 
                          : '1px solid rgba(139, 92, 246, 0.3)',
                        borderRadius: '6px',
                        backgroundColor: isSelected ? 'rgba(139, 92, 246, 0.1)' : '#1a1a1a',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ fontSize: '16px', marginBottom: '4px' }}>
                        {option.icon}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        fontWeight: '500',
                        color: '#fff',
                        marginBottom: '2px'
                      }}>
                        {option.name}
                      </div>
                      <div style={{ 
                        fontSize: '10px', 
                        color: '#ccc'
                      }}>
                        {option.description}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Negative Prompt */}
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontSize: '13px', 
                fontWeight: '500',
                color: '#fff'
              }}>
                Negative Prompt (What to avoid):
              </label>
              <textarea
                value={negativePrompt}
                onChange={(e) => onNegativePromptChange(e.target.value)}
                placeholder="blurry, low quality, distorted, watermark"
                maxLength={200}
                style={{
                  width: '100%',
                  minHeight: '60px',
                  padding: '10px',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '6px',
                  backgroundColor: '#1a1a1a',
                  color: 'white',
                  fontSize: '13px',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginTop: '4px'
              }}>
                <span style={{ fontSize: '11px', color: '#ccc' }}>
                  Describe what you don't want to see
                </span>
                <span style={{ 
                  fontSize: '11px', 
                  color: negativePrompt.length > 180 ? '#ef4444' : '#ccc'
                }}>
                  {negativePrompt.length}/200
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VeoAdvancedControls;